import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { viewportConfig } from './animations';
import { ArrowRight, Zap, Shield, Clock, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function CTASection() {
  const { t } = useTranslation();

  const quickWins = [
    { icon: Zap, text: t('landing.hero.trustBadge1') },
    { icon: Shield, text: t('landing.hero.trustBadge2') },
    { icon: Clock, text: t('landing.hero.trustBadge3') },
  ];

  return (
    <section className="py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div
          className="relative max-w-5xl mx-auto text-center rounded-[2rem] p-12 md:p-16 lg:p-20 text-white overflow-hidden"
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={viewportConfig}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          style={{
            background:
              'radial-gradient(circle at 15% 15%, rgba(255,255,255,0.18) 0%, transparent 45%), radial-gradient(circle at 85% 85%, rgba(6, 78, 59, 0.4) 0%, transparent 55%), linear-gradient(135deg, #10b981 0%, #0d9488 50%, #0f766e 100%)',
            boxShadow:
              '0 30px 90px -20px rgba(13, 148, 136, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.15) inset',
          }}
        >
          {/* Subtle grid overlay */}
          <div
            className="absolute inset-0 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage:
                'radial-gradient(ellipse at center, black 40%, transparent 75%)',
              WebkitMaskImage:
                'radial-gradient(ellipse at center, black 40%, transparent 75%)',
            }}
          />

          {/* OpenClaw watermark */}
          <motion.img
            src="/openclaw.svg"
            alt=""
            aria-hidden="true"
            className="absolute -right-16 -bottom-16 w-80 md:w-[28rem] opacity-[0.08] pointer-events-none select-none"
            animate={{ rotate: [0, 6, 0, -6, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.img
            src="/openclaw.svg"
            alt=""
            aria-hidden="true"
            className="absolute -left-20 -top-20 w-64 md:w-80 opacity-[0.07] pointer-events-none select-none"
            animate={{ rotate: [0, -8, 0, 8, 0] }}
            transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Ambient glow orbs */}
          <motion.div
            className="absolute -top-24 left-1/4 w-72 h-72 rounded-full pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(255, 255, 255, 0.35) 0%, transparent 70%)',
              filter: 'blur(60px)',
            }}
            animate={{ opacity: [0.4, 0.7, 0.4], scale: [1, 1.1, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute -bottom-24 right-1/4 w-80 h-80 rounded-full pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(6, 78, 59, 0.5) 0%, transparent 70%)',
              filter: 'blur(70px)',
            }}
            animate={{ opacity: [0.4, 0.7, 0.4], scale: [1.1, 1, 1.1] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          />

          <div className="relative z-10">
            {/* Emblem with OpenClaw logo */}
            <motion.div
              className="inline-block mb-8"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="relative">
                {/* Outer glow ring */}
                <motion.div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background:
                      'radial-gradient(circle, rgba(255, 255, 255, 0.5) 0%, transparent 65%)',
                    filter: 'blur(20px)',
                  }}
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.9, 0.5] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
                <div className="relative h-20 w-20 rounded-2xl bg-white/15 backdrop-blur-md border border-white/40 flex items-center justify-center mx-auto shadow-[0_10px_40px_-10px_rgba(255,255,255,0.5)]">
                  <img
                    src="/openclaw.svg"
                    alt="OpenClaw"
                    className="h-12 w-12"
                    style={{
                      filter:
                        'drop-shadow(0 2px 10px rgba(0, 0, 0, 0.25)) brightness(1.1)',
                    }}
                  />
                </div>
              </div>
            </motion.div>

            {/* Eyebrow */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/25 backdrop-blur-sm mb-6"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <Sparkles className="h-3.5 w-3.5 text-white" />
              <span className="text-xs font-semibold tracking-wider uppercase text-white">
                {t('landing.hero.badge')}
              </span>
            </motion.div>

            <motion.h2
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-[1.1] tracking-tight text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.15)]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t('landing.cta.title')}
            </motion.h2>

            <motion.p
              className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {t('landing.cta.subtitle')}
            </motion.p>

            {/* Quick wins */}
            <motion.div
              className="flex flex-wrap items-center justify-center gap-3 mb-10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {quickWins.map((item, i) => (
                <motion.div
                  key={i}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 border border-white/25 backdrop-blur-sm hover:bg-white/25 hover:border-white/40 transition-all"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.45 + i * 0.08 }}
                >
                  <item.icon className="h-4 w-4 text-white" />
                  <span className="text-sm font-medium text-white">
                    {item.text}
                  </span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.55 }}
            >
              <Link to="/register">
                <Button
                  size="xl"
                  className="group relative bg-white text-emerald-700 hover:bg-white hover:text-emerald-800 gap-2 font-semibold px-8 shadow-[0_15px_50px_-10px_rgba(0,0,0,0.25)] hover:shadow-[0_20px_60px_-10px_rgba(0,0,0,0.35)] transition-all duration-300 hover:scale-[1.02]"
                >
                  {t('landing.cta.button')}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <a href="/#features">
                <Button
                  size="xl"
                  variant="outline"
                  className="border border-white/40 bg-white/10 text-white hover:bg-white/20 hover:border-white/60 gap-2 backdrop-blur-sm font-semibold px-8 transition-all"
                >
                  {t('common.learnMore')}
                </Button>
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
